import type { ReactNode } from 'react';
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

type InsightsCardProps = {
  variant?: 'events' | 'publication' | 'whitepaper';
  href?: string | null;
  onClick?: () => void;
  external?: boolean;
  ariaLabel?: string;
  media?: ReactNode;
  badge?: string;
  title: string;
  description?: string;
  detail?: string;
  metaLeft: string;
  metaRight?: string;
};

export default function InsightsCard({
  variant = 'events',
  href,
  onClick,
  external,
  ariaLabel,
  media,
  badge,
  title,
  description,
  detail,
  metaLeft,
  metaRight,
}: InsightsCardProps) {
  const hasMedia = Boolean(media);
  const className = `insights-card${hasMedia ? '' : ' insights-card--text'}${onClick || href ? '' : ' insights-card--static'}`;

  const body = (
    <>
      {hasMedia ? (
        <div className={`insights-card__media insights-card__media--${variant}`}>
          {media}
          {badge ? (
            <span className={`insights-card__badge insights-card__badge--${variant}`}>{badge}</span>
          ) : null}
        </div>
      ) : null}
      <div className="insights-card__body">
        {badge && !hasMedia ? (
          <span
            className={`insights-card__badge insights-card__badge--inline insights-card__badge--${variant}`}
          >
            {badge}
          </span>
        ) : null}
        <h3 className="insights-card__title">{title}</h3>
        {description ? <p className="insights-card__description">{description}</p> : null}
        {detail ? <p className="insights-card__detail">{detail}</p> : null}
        <div className="insights-card__meta">
          <span className="insights-card__meta-left">{metaLeft}</span>
          {metaRight ? (
            <span className="insights-card__meta-right">
              {metaRight}
              {external ? (
                <ArrowTopRightOnSquareIcon className="insights-card__meta-icon" aria-hidden />
              ) : (
                <ArrowRightIcon className="insights-card__meta-icon" aria-hidden />
              )}
            </span>
          ) : null}
        </div>
      </div>
    </>
  );

  const label = ariaLabel ?? title;

  if (href) {
    return (
      <a
        href={href}
        className={className}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        aria-label={external ? `${label} (opens in a new tab)` : label}
      >
        {body}
      </a>
    );
  }

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} aria-label={label}>
        {body}
      </button>
    );
  }

  return <article className={className}>{body}</article>;
}
